import { IAction } from "../..";
import { UserAction } from "../../actions";
import { IStore } from "../../store-context";
import { FileRepoFilters, IEmail, Nullable } from "../../types";
import { IAccount, IAudiences, IBookmark, IRole, IUser, IUserModule, UserLight } from "../../types/user";
import { reducer } from "../index";

reducer.createHandler(
  UserAction.UpdateUserSuccess,
  (state: IStore, { payload }: IAction<IUser & { account: IAccount }>): IStore => {
    const { account, ...user } = payload;
    return {
      ...state,
      userModule: {
        ...state.userModule,
        user: {
          ...state.userModule.user,
          ...user
        },
        account: {
          ...state.userModule.account,
          ...account
        }
      }
    };
  }
);

reducer.createHandler(
  UserAction.GetRoleSuccess,
  (state: IStore, { payload }: IAction<IRole[]>): IStore => ({
    ...state,
    userModule: {
      ...state.userModule,
      roles: payload
    }
  })
);

reducer.createHandler(
  UserAction.GetAudienceSuccess,
  (state: IStore, { payload }: IAction<IAudiences>): IStore => ({
    ...state,
    userModule: {
      ...state.userModule,
      audiences: payload
    }
  })
);

reducer.createHandler(UserAction.GetIncomingEmailsSuccess, (state, { payload }: IAction<IEmail[]>) => ({
  ...state,
  userModule: {
    ...state.userModule,
    emails: payload
  }
}));

reducer.createHandler(UserAction.GetBookmarksSuccess, (state, { payload }: IAction<IBookmark[]>) => ({
  ...state,
  userModule: {
    ...state.userModule,
    bookmarks: payload
  }
}));

reducer.createHandler(UserAction.CreateBookmarkSuccess, (state, { payload }: IAction<IBookmark>) => ({
  ...state,
  userModule: {
    ...state.userModule,
    bookmarks:
      state.userModule.bookmarks.findIndex((item) => item.id === payload.id) < 0
        ? [payload, ...state.userModule.bookmarks]
        : state.userModule.bookmarks.map((item) => (item.id === payload.id ? payload : item)),
    selectedBookmark: payload.id
  }
}));

reducer.createHandler(UserAction.UpdateBookmarkSuccess, (state, { payload }: IAction<IBookmark>) => ({
  ...state,
  userModule: {
    ...state.userModule,
    bookmarks: state.userModule.bookmarks.map((item: IBookmark) => {
      if (item.id === payload.id) {
        return payload;
      } else if (payload.is_default && item.type === payload.type && item.report_type === payload.report_type) {
        return { ...item, is_default: false };
      }
      return item;
    })
  }
}));

reducer.createHandler(UserAction.DeleteBookmarkSuccess, (state, { payload }: IAction<number>) => ({
  ...state,
  userModule: {
    ...state.userModule,
    bookmarks: state.userModule.bookmarks.filter((item: IBookmark) => item.id !== payload)
  }
}));

reducer.createHandler(UserAction.SetSelectedBookmark, (state, { payload }: IAction<Nullable<IBookmark["id"]>>) => {
  return {
    ...state,
    userModule: {
      ...state.userModule,
      selectedBookmark: payload
    }
  };
});

reducer.createHandler(UserAction.GetManagersSuccess, (state, { payload }: IAction<UserLight[]>) => ({
  ...state,
  userModule: {
    ...state.userModule,
    managers: payload
  }
}));

reducer.createHandler(UserAction.SetShowUnlockFullAccess, (state, { payload }: IAction<boolean>) => ({
  ...state,
  userModule: {
    ...state.userModule,
    showUnlockFullAccess: payload
  }
}));

reducer.createHandler<IUserModule["numberFormat"]>(UserAction.SetNumberFormat, (state, { payload }) => ({
  ...state,
  userModule: {
    ...state.userModule,
    numberFormat: { ...payload }
  }
}));

reducer.createHandler<boolean>(UserAction.SetHideExtractorGuide, (state, { payload }) => ({
  ...state,
  userModule: {
    ...state.userModule,
    hideExtractorGuide: payload
  }
}));

reducer.createHandler<boolean>(UserAction.SetHideWelcomePopup, (state, { payload }) => ({
  ...state,
  userModule: {
    ...state.userModule,
    hideWelcomePopup: payload
  }
}));

reducer.createHandler<boolean>(UserAction.SetOnboardingCompleted, (state, { payload }) => ({
  ...state,
  userModule: {
    ...state.userModule,
    onboardingCompleted: payload
  }
}));

reducer.createHandler<IUserModule["workspaceColumns"]>(UserAction.UpdateWorkspaceColumns, (state, { payload }) => ({
  ...state,
  userModule: {
    ...state.userModule,
    workspaceColumns: { ...state.userModule.workspaceColumns, ...payload }
  }
}));

reducer.createHandler<IUserModule["pipelineViewConfig"]>(UserAction.SetPipelineViewConfig, (state, { payload }) => ({
  ...state,
  userModule: {
    ...state.userModule,
    pipelineViewConfig: payload
  }
}));

reducer.createHandler<IUserModule["amortizationTableColumns"]>(
  UserAction.UpdateAmortizationTableColumns,
  (state, { payload }) => ({
    ...state,
    userModule: {
      ...state.userModule,
      amortizationTableColumns: { ...state.userModule.amortizationTableColumns, ...payload }
    }
  })
);

reducer.createHandler(UserAction.IncrementNumberOfDeals, (state) => {
  if (!state.userModule.account.freemium_account_settings) {
    return state;
  }
  return {
    ...state,
    userModule: {
      ...state.userModule,
      account: {
        ...state.userModule.account,
        freemium_account_settings: {
          ...state.userModule.account.freemium_account_settings,
          number_of_deals: state.userModule.account.freemium_account_settings.number_of_deals + 1
        }
      }
    }
  };
});

reducer.createHandler(UserAction.DecrementNumberOfDeals, (state) => {
  if (!state.userModule.account.freemium_account_settings) {
    return state;
  }
  return {
    ...state,
    userModule: {
      ...state.userModule,
      account: {
        ...state.userModule.account,
        freemium_account_settings: {
          ...state.userModule.account.freemium_account_settings,
          number_of_deals: state.userModule.account.freemium_account_settings.number_of_deals - 1
        }
      }
    }
  };
});

reducer.createHandler<FileRepoFilters>(UserAction.SetFilesFilters, (state, { payload }) => ({
  ...state,
  userModule: {
    ...state.userModule,
    filesFilters: payload
  }
}));

reducer.createHandler<Record<string, boolean>>(UserAction.SetFileRepoColumns, (state, { payload }) => ({
  ...state,
  userModule: {
    ...state.userModule,
    fileRepoColumns: payload
  }
}));

reducer.createHandler<number | null>(UserAction.SetPortfolioConstruct, (state, { payload }) => ({
  ...state,
  userModule: {
    ...state.userModule,
    portfolioConstruct: payload
  }
}));

reducer.createHandler<IUserModule["digestSettings"]>(UserAction.SetDigestSettings, (state, { payload }) => ({
  ...state,
  userModule: {
    ...state.userModule,
    digestSettings: { ...state.userModule.digestSettings, ...payload }
  }
}));
